/* ui.definable.ai's Data Table, value for value, on an agent's table: a white card in a grey
   tray with the title and undo/redo on top and the add row, bulk actions and summary below.
   Cells select, ranges drag and extend with Shift, edit in place, copy and paste with any
   sheet, and undo and redo. Extended for a space: the header and the first column stay put
   while the rest scrolls, rows come from the server a page at a time (sorting is the
   server's), a header says a column's rules, and cells draw links, people and verified
   emails. Every write goes through `onWrite`, which saves it and reports what a rule refused. */
import React, { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowDown, Asterisk, KeyRound, Maximize2, MoreHorizontal, Plus, Redo2, Trash2, Undo2 } from "lucide-react";

import { IconButton } from "@/shared/components/ui-kit";
import { cn } from "@/shared/lib/cn";

import Cell from "./Cell";
import CellEditor, { type Move } from "./CellEditor";
import { ICONS, editsInPlace, keptOnRow, textOf, widthOf } from "./columns";
import { fromTsv, pastePlan, toTsv } from "./tsv";
import { useGridSelection, type Pos } from "./useGridSelection";

import type { SpaceColumn, SpaceRow, SpaceValue } from "@/shared/types";

/** One cell written: a value as typed, true/false for a checkbox, null to clear it. */
export interface CellWrite {
  rowId: string;
  key: string;
  value: string | boolean | null;
}

const HISTORY_MAX = 100;
const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const letter = (c: number) => String.fromCharCode(65 + (c % 26));

export default function DataTable({
  title, icon, sub, columns, rows, people, editable, sort, onSort, onWrite, onAddRow, onDeleteRows,
  onOpenRow, onColumnMenu, openColumn, pager, notice,
}: {
  title: string;
  icon: ReactNode;
  sub?: ReactNode;
  columns: SpaceColumn[];
  rows: SpaceRow[];
  people: Record<string, string | null>;
  /** false keeps selection and copy; no edit, paste, delete or add. */
  editable: boolean;
  sort?: string;
  onSort: (key: string) => void;
  onWrite: (writes: CellWrite[]) => Promise<void>;
  onAddRow?: () => void;
  onDeleteRows?: (ids: string[]) => void;
  onOpenRow: (rowId: string) => void;
  onColumnMenu?: (column: SpaceColumn, anchor: DOMRect) => void;
  /** The column whose editor is open: its header stays lit. */
  openColumn?: string | null;
  pager?: ReactNode;
  /** What the last write's rules said, in the footer. */
  notice?: ReactNode;
}) {
  const grid = useRef<HTMLDivElement>(null);
  const selection = useGridSelection(rows.length, columns.length);
  const { active, range } = selection;
  // the cell being edited, by row and column, not by place: a refetch may move the row, a
  // hidden column shift the columns, and the edit must still land where it was typed
  const [editing, setEditing] = useState<{ rowId: string; key: string; value: string } | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [backFromConfirm, setBackFromConfirm] = useState(false); // Keep or Escape: Delete takes the focus back
  const history = useRef<{ past: CellWrite[][]; future: CellWrite[][] }>({ past: [], future: [] });
  const [, redraw] = useState(0);

  // Rows that leave the screen (another page, a search, a run deleting one) leave the checked
  // set and the undo history, so a delete or an undo never reaches a row the person can't
  // see. Rows that only move (a sort after an edit) keep both.
  const shown = [...rows.map((row) => row.id)].sort().join(",");
  useEffect(() => {
    const ids = new Set(shown.split(","));
    const kept = (writes: CellWrite[]) => writes.every((w) => ids.has(w.rowId));
    setChecked((was) => ([...was].every((id) => ids.has(id)) ? was : new Set([...was].filter((id) => ids.has(id)))));
    history.current = { past: history.current.past.filter(kept), future: history.current.future.filter(kept) };
    redraw((n) => n + 1);
  }, [shown]);
  useEffect(() => {
    if (checked.size) return;
    setConfirming(false);
    setBackFromConfirm(false); // the next selection starts in the grid, its keys working
  }, [checked]);
  // where the edited cell is now; its row or column gone, the edit ends and the grid's keys work
  const editingAt: Pos | null = (() => {
    if (!editing) return null;
    const at = { r: rows.findIndex((row) => row.id === editing.rowId), c: columns.findIndex((column) => column.key === editing.key) };
    return at.r >= 0 && at.c >= 0 ? at : null;
  })();
  useEffect(() => {
    if (editing && !editingAt) setEditing(null);
  }, [editing, editingAt]);

  const canEdit = (c: number) => editable && editsInPlace(columns[c]);
  // a table unique by email is a list of people: their names get round avatars
  const rowsArePeople = columns.some((column) => column.type === "email" && column.rules.includes("unique"));
  const valueAt = (pos: Pos) => rows[pos.r]?.values[columns[pos.c].key];

  /** Write cells, remembering what they held so Undo can put it back. */
  const write = useCallback(
    async (writes: CellWrite[], remember = true) => {
      if (!writes.length) return;
      if (remember) {
        const before = writes.map((w) => {
          const row = rows.find((r) => r.id === w.rowId);
          const old = row?.values[w.key];
          return { ...w, value: typeof old === "boolean" ? old : old === null || old === undefined ? null : textOf(old) };
        });
        history.current.past = [...history.current.past.slice(-(HISTORY_MAX - 1)), before];
        history.current.future = [];
        redraw((n) => n + 1);
      }
      await onWrite(writes);
    },
    [rows, onWrite],
  );

  const undo = () => {
    const last = history.current.past.pop();
    if (!last) return;
    const now = last.map((w) => ({ ...w, value: textOrNull(rows.find((r) => r.id === w.rowId)?.values[w.key]) }));
    history.current.future.push(now);
    redraw((n) => n + 1);
    void write(last, false);
  };
  const redo = () => {
    const next = history.current.future.pop();
    if (!next) return;
    const now = next.map((w) => ({ ...w, value: textOrNull(rows.find((r) => r.id === w.rowId)?.values[w.key]) }));
    history.current.past.push(now);
    redraw((n) => n + 1);
    void write(next, false);
  };

  const startEdit = (pos: Pos, typed?: string) => {
    if (!canEdit(pos.c) || !rows[pos.r]) return;
    const column = columns[pos.c];
    if (column.type === "boolean") {
      void write([{ rowId: rows[pos.r].id, key: column.key, value: !valueAt(pos) }]);
      return;
    }
    setEditing({ rowId: rows[pos.r].id, key: column.key, value: typed ?? textOf(valueAt(pos)) });
  };

  const finishEdit = (save: boolean, value: string, move?: Move) => {
    const saved = editing && rows.find((row) => row.id === editing.rowId)?.values[editing.key];
    if (editing && save && value !== textOf(saved ?? undefined)) {
      void write([{ rowId: editing.rowId, key: editing.key, value: value.trim() === "" ? null : value }]);
    }
    if (editingAt && move) selection.moveTo({ r: editingAt.r + (move === "down" ? 1 : 0), c: editingAt.c + (move === "right" ? 1 : 0) });
    setEditing(null);
    requestAnimationFrame(() => grid.current?.focus({ preventScroll: true }));
  };

  /** The range's cells as rows of text, for the clipboard. */
  const rangeText = () => {
    if (!range) return "";
    const cells = [];
    for (let r = range.r1; r <= range.r2; r += 1) {
      const line = [];
      for (let c = range.c1; c <= range.c2; c += 1) line.push(textOf(rows[r]?.values[columns[c].key]));
      cells.push(line);
    }
    return toTsv(cells);
  };

  /** Every editable cell of the range cleared. */
  const clearRange = () => {
    if (!range || !editable) return;
    const writes: CellWrite[] = [];
    for (let r = range.r1; r <= range.r2; r += 1) {
      for (let c = range.c1; c <= range.c2; c += 1) {
        if (canEdit(c) && rows[r]) writes.push({ rowId: rows[r].id, key: columns[c].key, value: null });
      }
    }
    void write(writes);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (editing) return;
    const command = mac ? event.metaKey : event.ctrlKey;
    const key = event.key.toLowerCase();
    const handled = () => event.preventDefault();
    if (command && key === "z") {
      handled();
      if (event.shiftKey) redo();
      else undo();
      return;
    }
    if (command && key === "y") {
      handled();
      redo();
      return;
    }
    if (command && key === "a") {
      handled();
      selection.selectAll();
      return;
    }
    const at = selection.current.current.active;
    if (!at) {
      if (event.key.startsWith("Arrow") || event.key === "Enter") {
        handled();
        selection.moveTo({ r: 0, c: 0 });
      }
      return;
    }
    const jumps: Record<string, Pos> = {
      ArrowUp: { r: command ? 0 : at.r - 1, c: at.c },
      ArrowDown: { r: command ? rows.length - 1 : at.r + 1, c: at.c },
      ArrowLeft: { r: at.r, c: command ? 0 : at.c - 1 },
      ArrowRight: { r: at.r, c: command ? columns.length - 1 : at.c + 1 },
      Home: { r: command ? 0 : at.r, c: 0 },
      End: { r: command ? rows.length - 1 : at.r, c: columns.length - 1 },
    };
    if (jumps[event.key]) {
      handled();
      selection.moveTo(jumps[event.key], event.shiftKey);
    } else if (event.key === "Tab") {
      handled();
      selection.moveTo({ r: at.r, c: at.c + (event.shiftKey ? -1 : 1) });
    } else if (event.key === "Enter" || event.key === "F2" || (event.key === " " && columns[at.c].type === "boolean")) {
      handled();
      startEdit(at);
    } else if (event.key === "Escape") {
      selection.moveTo(at);
    } else if (event.key === "Delete" || event.key === "Backspace") {
      handled();
      clearRange();
    } else if (event.key.length === 1 && !command && !event.altKey && columns[at.c].type !== "choice") {
      // typing over a cell starts editing it with that character, as a sheet does
      handled();
      startEdit(at, event.key);
    }
  };

  const onCopy = (event: React.ClipboardEvent) => {
    if (editing || !range) return;
    event.preventDefault();
    event.clipboardData.setData("text/plain", rangeText());
  };
  const onCut = (event: React.ClipboardEvent) => {
    if (editing || !range) return;
    onCopy(event);
    clearRange();
  };
  const onPaste = (event: React.ClipboardEvent) => {
    if (editing || !range || !editable) return;
    event.preventDefault();
    const cells = pastePlan(fromTsv(event.clipboardData.getData("text/plain")), range);
    const writes = cells
      .filter(({ r, c }) => rows[r] && columns[c] && canEdit(c))
      .map(({ r, c, value }) => ({ rowId: rows[r].id, key: columns[c].key, value: value.trim() === "" ? null : value }));
    void write(writes);
  };

  const toggle = (ids: string[], on: boolean) =>
    setChecked((was) => {
      const next = new Set(was);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  const allChecked = rows.length > 0 && rows.every((row) => checked.has(row.id));
  const someChecked = !allChecked && rows.some((row) => checked.has(row.id));

  const summary = (() => {
    if (!range || !active) return null;
    if (range.r1 === range.r2 && range.c1 === range.c2) return `${letter(active.c)}${active.r + 1}`;
    let count = 0;
    let sum = 0;
    let numbers = false;
    for (let r = range.r1; r <= range.r2; r += 1) {
      for (let c = range.c1; c <= range.c2; c += 1) {
        const value = rows[r]?.values[columns[c].key];
        if (textOf(value) !== "") count += 1;
        if (columns[c].type === "number" && typeof value === "number") {
          numbers = true;
          sum += value;
        }
      }
    }
    return `${count} ${count === 1 ? "value" : "values"}${numbers ? ` · Sum ${sum.toLocaleString("en-US")}` : ""}`;
  })();

  return (
    <section aria-label={title} className="w-full min-w-0 h-full min-h-0 flex flex-col p-1.5 rounded-[22px] bg-[var(--dt-tray)] text-[var(--dt-ink)]">
      <header className="flex items-center gap-2.5 min-h-[46px] pl-[18px] pr-2.5 pt-[9px] pb-2 shrink-0">
        <span className="grid shrink-0">{icon}</span>
        <h3 className="m-0 text-[15px] font-medium leading-[22px] tracking-[-0.01em] truncate">{title}</h3>
        {sub}
        {editable && (
          <div className="ml-auto flex gap-0.5">
            <IconButton aria-label={`Undo (${mac ? "⌘" : "Ctrl"} Z)`} title={`Undo (${mac ? "⌘" : "Ctrl"} Z)`} disabled={!history.current.past.length} onClick={undo} className="disabled:opacity-35 disabled:pointer-events-none">
              <Undo2 className="w-[15px] h-[15px]" strokeWidth={1.6} />
            </IconButton>
            <IconButton aria-label={`Redo (${mac ? "⌘" : "Ctrl"} Shift Z)`} title={`Redo (${mac ? "⌘" : "Ctrl"} Shift Z)`} disabled={!history.current.future.length} onClick={redo} className="disabled:opacity-35 disabled:pointer-events-none">
              <Redo2 className="w-[15px] h-[15px]" strokeWidth={1.6} />
            </IconButton>
          </div>
        )}
      </header>
      <div className="flex-1 min-h-0 flex overflow-hidden rounded-2xl bg-[var(--dt-card)] border border-[var(--dt-border)] shadow-[var(--dt-card-shadow)]">
        <div
          ref={grid}
          role="grid"
          aria-label={title}
          aria-rowcount={rows.length + 1}
          aria-multiselectable="true"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onCopy={onCopy}
          onCut={onCut}
          onPaste={onPaste}
          className={cn("flex-1 min-w-0 overflow-auto outline-none overlay-scrollbar", selection.dragging && "cursor-cell select-none")}
        >
          <table role="presentation" className="w-full min-w-[640px] border-separate border-spacing-0 table-fixed text-[14px] leading-5">
            <colgroup>
              <col style={{ width: 44 }} />
              {columns.map((column, c) => <col key={column.key} style={{ width: widthOf(column, c === 0) }} />)}
            </colgroup>
            <thead>
              <tr role="row">
                <th role="columnheader" className={cn(HEAD, "sticky left-0 z-[3] p-0 text-center")}>
                  <Check
                    checked={allChecked}
                    mixed={someChecked}
                    label="Select all rows"
                    onChange={() => toggle(rows.map((row) => row.id), !allChecked)}
                  />
                </th>
                {columns.map((column, c) => {
                  const Icon = ICONS[column.type];
                  const direction = sort === column.key ? "asc" : sort === `-${column.key}` ? "desc" : null;
                  const lit = openColumn === column.key || (!!range && c >= range.c1 && c <= range.c2);
                  return (
                    <th
                      key={column.key}
                      role="columnheader"
                      aria-sort={direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none"}
                      className={cn(HEAD, "px-1.5 group/th", c === 0 && "sticky left-[44px] z-[3] shadow-[1px_0_0_var(--dt-grid)]", lit && "bg-[var(--dt-col-active)]")}
                    >
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => keptOnRow(column) && onSort(column.key)}
                          className={cn(
                            "press flex-1 min-w-0 flex items-center gap-2 h-8 px-2 rounded-lg text-left hover:bg-[var(--dt-th-hover)] hover:text-[var(--dt-text)]",
                            openColumn === column.key && "bg-[var(--dt-th-hover)] text-[var(--dt-text)]",
                          )}
                        >
                          <Icon className="w-[15px] h-[15px] shrink-0 text-[var(--dt-faint)]" strokeWidth={1.6} />
                          <span className="truncate">{column.name}</span>
                          <RuleMarks column={column} />
                          <ArrowDown
                            aria-hidden="true"
                            strokeWidth={1.6}
                            className={cn(
                              "w-[11px] h-[11px] ml-auto shrink-0 transition-[opacity,transform] duration-200",
                              direction ? "opacity-100 text-[var(--dt-accent-ink)]" : "opacity-0 group-hover/th:opacity-100 text-[var(--dt-faint)]",
                              direction === "asc" && "rotate-180",
                            )}
                          />
                        </button>
                        {onColumnMenu && (
                          <button
                            type="button"
                            aria-label={`${column.name}: column settings`}
                            onClick={(event) => onColumnMenu(column, (event.currentTarget.closest("th") as HTMLElement).getBoundingClientRect())}
                            className={cn(
                              "press w-6 h-6 shrink-0 grid place-items-center rounded-md text-[var(--dt-faint)] hover:text-[var(--dt-text)] hover:bg-[var(--dt-th-hover)]",
                              openColumn === column.key ? "opacity-100" : "opacity-0 group-hover/th:opacity-100 focus-visible:opacity-100",
                            )}
                          >
                            <MoreHorizontal className="w-3.5 h-3.5" strokeWidth={1.75} />
                          </button>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => {
                const isChecked = checked.has(row.id);
                return (
                  <tr key={row.id} role="row" data-selected={isChecked || undefined} className="group/row">
                    <td role="gridcell" className={cn(BODY, "sticky left-0 z-[1] p-0 text-center", rowFill(isChecked))}>
                      <Check checked={isChecked} label={`Select ${textOf(row.values[columns[0]?.key]) || "row"}`} onChange={() => toggle([row.id], !isChecked)} />
                    </td>
                    {columns.map((column, c) => {
                      const isActive = active?.r === r && active?.c === c;
                      const isEditing = editingAt?.r === r && editingAt?.c === c;
                      const shaded = selection.inRange(r, c) && !isActive;
                      return (
                        <td
                          key={column.key}
                          role="gridcell"
                          aria-selected={selection.inRange(r, c)}
                          aria-readonly={!canEdit(c) || undefined}
                          className={cn(
                            BODY,
                            "px-3.5 cursor-cell",
                            c === 0 && "sticky left-[44px] z-[1] shadow-[1px_0_0_var(--dt-grid)]",
                            shaded ? "bg-[var(--dt-range)]" : rowFill(isChecked),
                            isEditing && "p-0 overflow-visible bg-[var(--dt-card)]",
                          )}
                          onPointerDown={(event) => {
                            if (isEditing || event.button !== 0 || (event.target as HTMLElement).closest("a,button")) return;
                            event.preventDefault();
                            // an open editor saves what it holds as it loses focus here
                            grid.current?.focus({ preventScroll: true });
                            selection.moveTo({ r, c }, event.shiftKey);
                            selection.setDragging(true);
                          }}
                          onPointerEnter={() => selection.dragOver({ r, c })}
                          onDoubleClick={() => (canEdit(c) ? startEdit({ r, c }) : onOpenRow(row.id))}
                        >
                          {isActive && <span aria-hidden="true" className={cn("pointer-events-none absolute inset-0 rounded-[3px] border-2", "border-[var(--dt-accent)]")} />}
                          {isEditing && editing ? (
                            <CellEditor
                              column={column}
                              initial={editing.value}
                              onDraft={(value) => setEditing({ ...editing, value })}
                              onDone={finishEdit}
                            />
                          ) : (
                            <span className="flex items-center gap-1.5 min-w-0">
                              <span className="flex-1 min-w-0 truncate">
                                <Cell column={column} row={row} first={c === 0} people={people} rowsArePeople={rowsArePeople} />
                              </span>
                              {c === 0 && (
                                <button
                                  type="button"
                                  aria-label={`Open ${textOf(row.values[column.key]) || "row"}`}
                                  onClick={() => onOpenRow(row.id)}
                                  className="press shrink-0 w-6 h-6 grid place-items-center rounded-md text-[var(--dt-faint)] opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 hover:text-[var(--dt-ink)] hover:bg-[var(--dt-wash)]"
                                >
                                  <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <p className="m-0 p-6 text-center text-[14px] text-[var(--dt-muted)]">No rows yet</p>}
        </div>
      </div>
      <footer className="flex items-center justify-between gap-3 min-h-[44px] pl-2 pr-2.5 pt-1.5 pb-[7px] shrink-0">
        {checked.size > 0 ? (
          <div
            role="status"
            className="flex items-center gap-1 min-w-0 pl-2.5 text-[14px] font-medium"
            onKeyDown={(event) => {
              if (event.key === "Escape" && confirming) {
                event.stopPropagation();
                setConfirming(false);
                setBackFromConfirm(true);
              }
            }}
          >
            {/* keyed: each side mounts afresh, so the button it focuses takes the focus */}
            {confirming ? (
              <React.Fragment key="confirm">
                <span className="mr-1.5 truncate text-[var(--danger)]">Delete {checked.size} for good?</span>
                <FootButton
                  solid
                  focus
                  onClick={() => {
                    onDeleteRows?.([...checked]);
                    setChecked(new Set());
                    grid.current?.focus({ preventScroll: true });
                  }}
                >
                  Delete
                </FootButton>
                <FootButton
                  onClick={() => {
                    setConfirming(false);
                    setBackFromConfirm(true);
                  }}
                >
                  Keep
                </FootButton>
              </React.Fragment>
            ) : (
              <React.Fragment key="selected">
                <span className="mr-1.5">{checked.size} selected</span>
                {editable && onDeleteRows && (
                  <FootButton
                    danger
                    focus={backFromConfirm}
                    onClick={() => {
                      setConfirming(true);
                      setBackFromConfirm(false);
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} /> Delete
                  </FootButton>
                )}
                <FootButton onClick={() => setChecked(new Set())}>Clear</FootButton>
              </React.Fragment>
            )}
          </div>
        ) : editable && onAddRow ? (
          <FootButton onClick={onAddRow}>
            <Plus className="w-3.5 h-3.5" strokeWidth={1.6} /> Add row
          </FootButton>
        ) : (
          <span />
        )}
        <span aria-live="polite" className="flex items-center gap-2.5 min-w-0 text-[13px] tabular-nums text-[var(--dt-muted)]">
          {notice}
          {summary && <span className="font-mono">{summary}</span>}
          {summary && pager && <span className="opacity-50">·</span>}
          {pager}
        </span>
      </footer>
    </section>
  );
}

const HEAD = "relative h-11 border-l first:border-l-0 border-[var(--dt-grid)] text-left align-middle whitespace-nowrap font-[450] text-[var(--dt-muted)] bg-[var(--dt-card)] sticky top-0 z-[2] transition-colors";
const BODY = "relative h-11 border-t border-l first:border-l-0 border-[var(--dt-grid)] text-left align-middle whitespace-nowrap overflow-hidden text-ellipsis transition-colors";

/** A row's cells: hover and checked fills the sticky cells must paint themselves. */
const rowFill = (checked: boolean) =>
  checked ? "bg-[var(--dt-selected)]" : "bg-[var(--dt-card)] group-hover/row:bg-[var(--dt-hover)]";

function textOrNull(value: SpaceValue | undefined): string | boolean | null {
  if (typeof value === "boolean") return value;
  return value === null || value === undefined ? null : textOf(value);
}

/** The column's rules at a glance: a key for unique, an asterisk for required. */
function RuleMarks({ column }: { column: SpaceColumn }) {
  return (
    <>
      {column.rules.includes("unique") && (
        <span title="Unique" className="inline-flex shrink-0">
          <KeyRound aria-label="Unique" className="w-3 h-3 text-[var(--dt-faint)]" strokeWidth={1.75} />
        </span>
      )}
      {column.rules.includes("required") && (
        <span title="Required" className="inline-flex shrink-0">
          <Asterisk aria-label="Required" className="w-3 h-3 text-[var(--dt-faint)]" strokeWidth={1.75} />
        </span>
      )}
    </>
  );
}

/** The footer's buttons, the Data Table's size: `danger` in red, `solid` the delete that
    confirms (the colours of `confirmDangerCls`), `focus` taking the focus as it appears. */
function FootButton({ danger, solid, focus, onClick, children }: {
  danger?: boolean;
  solid?: boolean;
  focus?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      autoFocus={focus}
      onClick={onClick}
      className={cn(
        "press inline-flex shrink-0 items-center gap-2 h-[30px] px-2.5 rounded-lg text-[14px]",
        solid
          ? "bg-[var(--danger)] text-white hover:bg-[var(--danger-hover)]"
          : danger
            ? "text-[var(--danger)] hover:bg-[var(--danger-bg)]"
            : "text-[var(--dt-muted)] hover:bg-[var(--dt-wash)] hover:text-[var(--dt-ink)]",
      )}
    >
      {children}
    </button>
  );
}

function Check({ checked, mixed, label, onChange }: { checked: boolean; mixed?: boolean; label: string; onChange: () => void }) {
  const on = checked || mixed;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={mixed ? "mixed" : checked}
      aria-label={label}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={onChange}
      className={cn(
        "mx-auto grid place-items-center w-4 h-4 rounded-[5px] border transition-colors",
        on ? "bg-[var(--dt-ink)] border-[var(--dt-ink)]" : "bg-[var(--dt-card)] border-[var(--dt-check-border)] hover:border-[var(--dt-muted)]",
      )}
    >
      <svg viewBox="0 0 16 16" className={cn("w-3 h-3 fill-none stroke-[var(--dt-card)] [stroke-width:1.8] [stroke-linecap:round] [stroke-linejoin:round] transition-[opacity,transform] duration-200", on ? "opacity-100 scale-100" : "opacity-0 scale-[0.6]")}>
        <path d={mixed ? "M4.5 8h7" : "m4.25 8.25 2.5 2.5 5-5.5"} />
      </svg>
    </button>
  );
}
